import { uniqBy } from 'lodash';
import React__default, { useState, useRef } from 'react';
import { v4 } from 'uuid';
import { toOption } from '@grafana/data';
import { selectors } from '@grafana/e2e-selectors';
import { InlineField, Select } from '@grafana/ui';
import { InputGroup } from '../../QueryEditor/InputGroup.js';
import { AccessoryButton } from '../../QueryEditor/AccessoryButton.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const CONFLICTING_LABEL_FILTER_ERROR_MESSAGE = "You have conflicting label filters";
function LabelFilterItem({
  item,
  items,
  defaultOp,
  onChange,
  onDelete,
  onGetLabelNames,
  onGetLabelValues,
  invalidLabel,
  invalidValue,
  multiValueSeparator = "|"
}) {
  var _a;
  const [state, setState] = useState({});
  const [labelNamesMenuOpen, setLabelNamesMenuOpen] = useState(false);
  const [labelValuesMenuOpen, setLabelValuesMenuOpen] = useState(false);
  const isMultiSelect = (operator = item.op) => {
    var _a2;
    return (_a2 = operators.find((op) => op.label === operator)) == null ? void 0 : _a2.isMultiValue;
  };
  const getSelectOptionsFromString = (item2) => {
    if (item2) {
      if (item2.indexOf(multiValueSeparator) > 0) {
        return item2.split(multiValueSeparator);
      }
      return [item2];
    }
    return [];
  };
  const getOptions = () => {
    const labelValues = state.labelValues ? [...state.labelValues] : [];
    const selectedOptions = getSelectOptionsFromString(item == null ? void 0 : item.value).map(toOption);
    return uniqBy([...selectedOptions, ...labelValues], "value");
  };
  const isConflicting = isConflictingLabelFilter(item, items);
  const { current: id } = useRef(v4());
  return /* @__PURE__ */ React__default.createElement("div", { "data-testid": "visual-query-builder-dimensions-filter-item" }, /* @__PURE__ */ React__default.createElement(InlineField, { error: CONFLICTING_LABEL_FILTER_ERROR_MESSAGE, invalid: isConflicting ? true : void 0 }, /* @__PURE__ */ React__default.createElement(InputGroup, null, /* @__PURE__ */ React__default.createElement(
    Select,
    {
      placeholder: "Select label",
      "data-testid": selectors.components.QueryBuilder.labelSelect,
      inputId: `visual-query-builder-dimensions-filter-item-key-${id}`,
      width: "auto",
      value: item.label ? toOption(item.label) : null,
      allowCustomValue: true,
      onOpenMenu: async () => {
        setState({ isLoadingLabelNames: true });
        const labelNames = await onGetLabelNames(item);
        setLabelNamesMenuOpen(true);
        setState({ labelNames, isLoadingLabelNames: void 0 });
      },
      onCloseMenu: () => {
        setLabelNamesMenuOpen(false);
      },
      isOpen: labelNamesMenuOpen,
      isLoading: state.isLoadingLabelNames,
      options: state.labelNames,
      onChange: (change) => {
        var _a2;
        if (change.value) {
          onChange(__spreadProps(__spreadValues({}, item), {
            op: (_a2 = item.op) != null ? _a2 : defaultOp,
            label: change.value
          }));
        }
      },
      invalid: isConflicting || invalidLabel
    }
  ), /* @__PURE__ */ React__default.createElement(
    Select,
    {
      "data-testid": selectors.components.QueryBuilder.matchOperatorSelect,
      value: toOption((_a = item.op) != null ? _a : defaultOp),
      options: operators,
      width: "auto",
      onChange: (change) => {
        if (change.value) {
          onChange(__spreadProps(__spreadValues({}, item), {
            op: change.value,
            value: isMultiSelect(change.value) ? item.value : getSelectOptionsFromString(item == null ? void 0 : item.value)[0]
          }));
        }
      },
      invalid: isConflicting
    }
  ), /* @__PURE__ */ React__default.createElement(
    Select,
    {
      placeholder: "Select value",
      "data-testid": selectors.components.QueryBuilder.valueSelect,
      inputId: `visual-query-builder-dimensions-filter-item-value-${id}`,
      width: "auto",
      value: isMultiSelect() ? getSelectOptionsFromString(item == null ? void 0 : item.value).map(toOption) : getSelectOptionsFromString(item == null ? void 0 : item.value).map(toOption)[0],
      allowCustomValue: true,
      onOpenMenu: async () => {
        setState({ isLoadingLabelValues: true });
        const labelValues = await onGetLabelValues(item);
        setState(__spreadProps(__spreadValues({}, state), {
          labelValues,
          isLoadingLabelValues: void 0
        }));
        setLabelValuesMenuOpen(true);
      },
      onCloseMenu: () => {
        setLabelValuesMenuOpen(false);
      },
      isOpen: labelValuesMenuOpen,
      isMulti: isMultiSelect(),
      isLoading: state.isLoadingLabelValues,
      options: getOptions(),
      onChange: (change) => {
        var _a2, _b;
        if (change.value) {
          onChange(__spreadProps(__spreadValues({}, item), {
            value: change.value,
            op: (_a2 = item.op) != null ? _a2 : defaultOp
          }));
        } else {
          const changes = change.map((change2) => {
            if (change2.value) {
              return change2.value;
            } else {
              return void 0;
            }
          }).filter((val) => val !== void 0).join(multiValueSeparator);
          onChange(__spreadProps(__spreadValues({}, item), { value: changes, op: (_b = item.op) != null ? _b : defaultOp }));
        }
      },
      invalid: isConflicting || invalidValue
    }
  ), /* @__PURE__ */ React__default.createElement(AccessoryButton, { "aria-label": "remove", icon: "times", variant: "secondary", onClick: onDelete }))));
}
const operators = [
  { label: "=", value: "=", description: "Equals", isMultiValue: false },
  { label: "!=", value: "!=", description: "Does not equal", isMultiValue: false },
  { label: "=~", value: "=~", description: "Matches regex", isMultiValue: true },
  { label: "!~", value: "!~", description: "Does not match regex", isMultiValue: true }
];
function isConflictingLabelFilter(newLabel, labels) {
  if (!newLabel.label || !newLabel.op || !newLabel.value) {
    return false;
  }
  if (labels.length < 2) {
    return false;
  }
  const operationIsNegative = newLabel.op.toString().startsWith("!");
  const candidates = labels.filter(
    (label) => label.label === newLabel.label && label.value === newLabel.value && label.op !== newLabel.op
  );
  const conflict = candidates.some((candidate) => {
    var _a, _b;
    if (operationIsNegative && ((_a = candidate == null ? void 0 : candidate.op) == null ? void 0 : _a.toString().startsWith("!")) === false) {
      return true;
    }
    if (operationIsNegative === false && ((_b = candidate == null ? void 0 : candidate.op) == null ? void 0 : _b.toString().startsWith("!"))) {
      return true;
    }
    return false;
  });
  return conflict;
}

export { LabelFilterItem, isConflictingLabelFilter };
//# sourceMappingURL=LabelFilterItem.js.map
